import { z } from 'zod';

/**
 * ACCESS ROLES: who may do what in this instance (`docs/decisions/roles-and-permissions.md`).
 *
 * It lives in `shared` because three consumers must agree on one vocabulary: the server
 * enforces it, the browser renders it, and migration 0013 seeds it. A flag spelled one way
 * in the seed and another way in a route guard is a permission that silently never holds.
 *
 * A ROLE IS A NAMED BUNDLE (D1): a label, a set of permission flags, four record verbs and
 * optional per-object overrides. A principal - a user or an API key - holds N roles and
 * their permissions UNION additively (D2). There are no negative permissions and no
 * per-user overrides: exclusion is by not granting.
 *
 * THE WORD "ROLE" MEANS TWO THINGS IN THIS PRODUCT (m9). The OFMOS seed has a `role` FIELD
 * on `team_member`, which is Ben's business vocabulary. This is the platform concept, and
 * every user-facing string calls it an ACCESS ROLE so the two stay apart in a sentence.
 */

/**
 * D3: every flag has a consumer today. A flag gating nothing is RESERVED in the decision
 * document and deliberately absent here until its surface exists, so `BILLING`,
 * `IMPERSONATE` and `AUDIT_LOG` are still not in this list.
 *
 * `EXPORT_CSV` and `IMPORT_CSV` LEFT that list on 2026-08-04: the CSV module is their
 * surface (`csv-import-export.md` D33), and both are enforced server-side on every route
 * in its section 10, re-resolved when the worker picks a run up, and checked again on
 * every download.
 *
 * CORRECTION, CARRIED OUT WITH THAT CHANGE (csv-import-export.md D33 and section 13).
 * The rule above was attributed here to `admin-area.md:1436`. That line says the opposite
 * thing - it is a gap-table row reading "Field-level permissions UI | `records/access.ts`
 * `createRoleFieldAccess` is a documented stub; the UI must not ship before the
 * enforcement it implies", which forbids a SURFACE WITHOUT ENFORCEMENT rather than a flag
 * with no surface. The rule belongs to `roles-and-permissions.md` D3, and `admin-area.md`
 * is a DRAFT either way.
 */
export const PERMISSION_FLAGS = [
  'DATA_MODEL',
  'WORKSPACE_MEMBERS',
  'API_KEYS',
  'WEBHOOKS',
  'WORKFLOWS_MANAGE',
  'WORKFLOWS_RUN',
  'IDENTITY',
  'AGENT_POLICY',
  'PENDING_DECIDE',
  'SECURITY',
  'ROLES',
  'IMPORT_CSV',
  'EXPORT_CSV',
  'REPORTS_MANAGE',
  /**
   * THE TWO INBOUND-INTAKE FLAGS (`inbound-intake.md` D2), and they are split because they
   * are held by different KINDS of principal rather than by different people.
   *
   * `INTAKE_MANAGE` configures sources: it is a settings permission and belongs to a person.
   * `INTAKE_SUBMIT` delivers to one: it is held by the source's OWN API key and by
   * nothing else, on a role the mint derives from the mapping. Splitting them is what makes
   * the receipt route's rule expressible at all - a key that may deliver must not be able to
   * rewrite the mapping that decides where its delivery lands.
   */
  'INTAKE_MANAGE',
  'INTAKE_SUBMIT',
  /**
   * THE HEALTH RULES (`phase-b-coach.md` D12), and it is a SETTINGS permission rather than a
   * data one. Reading a colour and its reasons is not gated at all - it is a field on a
   * record and travels with that record's own permissions - but the rules that WRITE the
   * colour decide what every coach sees as burning, so changing them is the same line the
   * flags above draw: Operator and Owner.
   */
  'HEALTH_MANAGE',
] as const;

export type PermissionFlag = (typeof PERMISSION_FLAGS)[number];

export const permissionFlagSchema = z.literal([...PERMISSION_FLAGS]);

export function isPermissionFlag(value: string): value is PermissionFlag {
  return (PERMISSION_FLAGS as readonly string[]).includes(value);
}

/**
 * D4: the scope of a record verb, per object.
 *
 * `team` is in the enum and NO TEAM ENTITY EXISTS (section 7), so a role set to `team`
 * behaves as `none` until one lands. The UI must not offer it; `TEAM_SCOPE_IS_INERT` is
 * what both sides check rather than each remembering.
 */
export const RECORD_SCOPES = ['all', 'team', 'own', 'none'] as const;
export type RecordScope = (typeof RECORD_SCOPES)[number];
export const recordScopeSchema = z.literal([...RECORD_SCOPES]);

/** Section 7: no team entity, so `team` grants nothing yet. Offered by no surface. */
export const TEAM_SCOPE_IS_INERT = true;

/** Most permissive wins (D2 rule 1, D16 composition rule 4). Higher index is wider. */
const SCOPE_RANK: Readonly<Record<RecordScope, number>> = {
  none: 0,
  own: 1,
  team: 2,
  all: 3,
};

export function widerScope(left: RecordScope, right: RecordScope): RecordScope {
  return SCOPE_RANK[left] >= SCOPE_RANK[right] ? left : right;
}

/**
 * The four verbs a role carries (D3). CREATE is deliberately not one of them: it is
 * governed by `update`, following Twenty, and `RecordAccess.canCreate` on the server says
 * so at the one place it matters.
 *
 * RESTORE is governed by `softDelete`, for the same reason: undoing a delete is the same
 * authority as doing one, and a separate verb would let a role delete what it cannot put
 * back.
 */
export const RECORD_VERBS = ['read', 'update', 'softDelete', 'destroy'] as const;
export type RecordVerb = (typeof RECORD_VERBS)[number];
export const recordVerbSchema = z.literal([...RECORD_VERBS]);

/** The role-level boolean backing each verb, spelled as Twenty spells it (m4). */
export const RECORD_VERB_COLUMNS: Readonly<Record<RecordVerb, string>> = {
  read: 'canReadAllObjectRecords',
  update: 'canUpdateAllObjectRecords',
  softDelete: 'canSoftDeleteAllObjectRecords',
  destroy: 'canDestroyAllObjectRecords',
};

/**
 * A per-object deviation from the role's defaults (D6, D16).
 *
 * EVERY COLUMN IS NULLABLE AND NULL MEANS INHERIT. Narrowing one verb on one object must
 * not force explicit values for the other three, and an absent row must be identical to a
 * row of all NULLs (composition rule 3).
 */
export interface RoleObjectPermission {
  readonly objectMetadataId: string;
  readonly canRead: RecordScope | null;
  readonly canUpdate: RecordScope | null;
  readonly canSoftDelete: RecordScope | null;
  readonly canDestroy: RecordScope | null;
  /**
   * HubSpot's `Unassigned` companion (D5): an `own` scope may additionally match rows
   * whose `assigned_user_id` is null. Without it every agent-created lead is invisible to
   * the setter who has to work it. Null means inherit, and the inherited value is false.
   */
  readonly includeUnassigned: boolean | null;
}

export interface Role {
  readonly id: string;
  readonly label: string;
  readonly description: string | null;
  /** Guardrail 3: `Operator` is false and every server-side mutation of it refuses. */
  readonly isEditable: boolean;
  /**
   * m7: Twenty's short-circuit. A role carrying this passes EVERY flag check without a
   * `role_flag` row, so a flag added next year is automatically held by `Operator` and
   * needs no data migration. Every other role enumerates its flags.
   */
  readonly canUpdateAllSettings: boolean;
  readonly canBeAssignedToUsers: boolean;
  readonly canBeAssignedToApiKeys: boolean;
  readonly canReadAllObjectRecords: boolean;
  readonly canUpdateAllObjectRecords: boolean;
  readonly canSoftDeleteAllObjectRecords: boolean;
  readonly canDestroyAllObjectRecords: boolean;
  readonly flags: readonly PermissionFlag[];
  readonly objectPermissions: readonly RoleObjectPermission[];
  readonly updatedAt: string;
  readonly updatedBy: string | null;
}

/** What a member row shows: the role, without its object table. */
export interface RoleSummary {
  readonly id: string;
  readonly label: string;
  readonly isEditable: boolean;
  readonly canBeAssignedToUsers: boolean;
  readonly canBeAssignedToApiKeys: boolean;
}

export function toRoleSummary(role: Role): RoleSummary {
  return {
    id: role.id,
    label: role.label,
    isEditable: role.isEditable,
    canBeAssignedToUsers: role.canBeAssignedToUsers,
    canBeAssignedToApiKeys: role.canBeAssignedToApiKeys,
  };
}

// --- the union -------------------------------------------------------------

export type ScopesByVerb = Readonly<Record<RecordVerb, RecordScope>>;

/**
 * What one principal may do, after folding every role it holds (D2).
 *
 * It is PLAIN DATA rather than an object with methods, because it crosses the wire: the
 * "Permissions" panel (section 6) renders the effective union, and a class with
 * behaviour would have to be rebuilt on the other side.
 *
 * `objects` carries only objects some role deviates on. Every other object answers from
 * `records`, which is why `scopeFor` exists rather than a lookup at each call site.
 */
export interface PermissionSet {
  /** m7: true short-circuits every flag check. */
  readonly allSettings: boolean;
  readonly flags: readonly PermissionFlag[];
  readonly records: ScopesByVerb;
  readonly objects: Readonly<Record<string, ScopesByVerb>>;
  readonly includeUnassigned: Readonly<Record<string, boolean>>;
}

/** Zero roles, zero permissions. What an unassigned API key resolves to (D18 step 4). */
export const NO_PERMISSIONS: PermissionSet = {
  allSettings: false,
  flags: [],
  records: { read: 'none', update: 'none', softDelete: 'none', destroy: 'none' },
  objects: {},
  includeUnassigned: {},
};

export function hasFlag(permissions: PermissionSet, flag: PermissionFlag): boolean {
  return permissions.allSettings || permissions.flags.includes(flag);
}

export function scopeFor(
  permissions: PermissionSet,
  objectId: string,
  verb: RecordVerb,
): RecordScope {
  return permissions.objects[objectId]?.[verb] ?? permissions.records[verb];
}

export function includeUnassignedFor(permissions: PermissionSet, objectId: string): boolean {
  return permissions.includeUnassigned[objectId] ?? false;
}

/** D16 composition rule 2: the role-level boolean is `all` or `none`, never a scope. */
function roleDefault(role: Role, verb: RecordVerb): RecordScope {
  switch (verb) {
    case 'read':
      return role.canReadAllObjectRecords ? 'all' : 'none';
    case 'update':
      return role.canUpdateAllObjectRecords ? 'all' : 'none';
    case 'softDelete':
      return role.canSoftDeleteAllObjectRecords ? 'all' : 'none';
    case 'destroy':
      return role.canDestroyAllObjectRecords ? 'all' : 'none';
    default: {
      const unhandled: never = verb;
      throw new Error(`unknown record verb ${String(unhandled)}`);
    }
  }
}

function overrideOf(entry: RoleObjectPermission, verb: RecordVerb): RecordScope | null {
  switch (verb) {
    case 'read':
      return entry.canRead;
    case 'update':
      return entry.canUpdate;
    case 'softDelete':
      return entry.canSoftDelete;
    case 'destroy':
      return entry.canDestroy;
    default: {
      const unhandled: never = verb;
      throw new Error(`unknown record verb ${String(unhandled)}`);
    }
  }
}

/**
 * D16 composition rules 1-3, for ONE role: a non-null override column wins outright,
 * including when it NARROWS; otherwise the role-level boolean decides.
 *
 * The permissive-direction rule (rule 4) applies only ACROSS roles, never between a role
 * and its own override - which is what makes "this role sees everything except invoices"
 * expressible at all.
 */
export function roleScopeFor(role: Role, objectId: string, verb: RecordVerb): RecordScope {
  const entry = role.objectPermissions.find((row) => row.objectMetadataId === objectId);
  const override = entry === undefined ? null : overrideOf(entry, verb);
  return override ?? roleDefault(role, verb);
}

/**
 * THE UNION (D2, D16 rule 4). Adding a role can never reduce access, which is a property of
 * this function and is pinned by test 1.
 *
 * Every object any role deviates on is folded across ALL roles, not only the ones carrying
 * an override row: a role with a narrow override on `invoice` plus a second role with
 * role-wide read must still read invoices, and computing `objects` from override rows alone
 * would have silently produced the opposite.
 */
export function unionPermissions(roles: readonly Role[]): PermissionSet {
  const flags = new Set<PermissionFlag>();
  let allSettings = false;
  const records: Record<RecordVerb, RecordScope> = {
    read: 'none',
    update: 'none',
    softDelete: 'none',
    destroy: 'none',
  };
  const touchedObjects = new Set<string>();

  for (const role of roles) {
    if (role.canUpdateAllSettings) allSettings = true;
    for (const flag of role.flags) flags.add(flag);
    for (const verb of RECORD_VERBS) {
      records[verb] = widerScope(records[verb], roleDefault(role, verb));
    }
    for (const entry of role.objectPermissions) touchedObjects.add(entry.objectMetadataId);
  }

  const objects: Record<string, ScopesByVerb> = {};
  const includeUnassigned: Record<string, boolean> = {};
  for (const objectId of touchedObjects) {
    const scopes: Record<RecordVerb, RecordScope> = {
      read: 'none',
      update: 'none',
      softDelete: 'none',
      destroy: 'none',
    };
    let unassigned = false;
    for (const role of roles) {
      for (const verb of RECORD_VERBS) {
        scopes[verb] = widerScope(scopes[verb], roleScopeFor(role, objectId, verb));
      }
      const entry = role.objectPermissions.find((row) => row.objectMetadataId === objectId);
      if (entry?.includeUnassigned === true) unassigned = true;
    }
    objects[objectId] = scopes;
    if (unassigned) includeUnassigned[objectId] = true;
  }

  return {
    allSettings,
    flags: [...flags].sort(),
    records,
    objects,
    includeUnassigned,
  };
}

/**
 * GUARDRAIL 1, THE ESCALATION CEILING, as a pure predicate so both the server check and the
 * UI's "disabled with a reason" rendering read the same rule.
 *
 * A grantor may only hand out what they hold: flags, the `allSettings` short-circuit, and
 * record scopes no wider than their own. `Operator` (allSettings) may grant anything.
 */
export interface GrantViolation {
  readonly kind: 'flag' | 'allSettings' | 'record' | 'object' | 'unassigned';
  readonly flag?: PermissionFlag | undefined;
  readonly verb?: RecordVerb | undefined;
  readonly objectMetadataId?: string | undefined;
}

export function grantViolations(
  actor: PermissionSet,
  target: Pick<
    Role,
    | 'canUpdateAllSettings'
    | 'canReadAllObjectRecords'
    | 'canUpdateAllObjectRecords'
    | 'canSoftDeleteAllObjectRecords'
    | 'canDestroyAllObjectRecords'
    | 'flags'
    | 'objectPermissions'
  >,
): readonly GrantViolation[] {
  /**
   * THE ROLE IS FOLDED INTO A `PermissionSet` AND THE COMPARISON IS `coverageViolations`.
   *
   * "May you hand this role out" and "may you act on this principal" are the same question
   * asked of a different target, and asking it twice in two loops is how the two answers
   * drift - one of them would eventually learn about a new verb and the other would not.
   * The union of one role is exactly what this function used to compute inline.
   */
  const asRole: Role = {
    id: '',
    label: '',
    description: null,
    isEditable: true,
    updatedAt: '',
    updatedBy: null,
    canBeAssignedToUsers: true,
    canBeAssignedToApiKeys: true,
    ...target,
  };
  return coverageViolations(actor, unionPermissions([asRole]));
}

/**
 * GUARDRAIL 8, THE TARGET CEILING (wave 5, D-F3.1), and it is guardrail 1 turned around: an
 * administrative action ON a person or a key is refused unless the actor's effective
 * permissions COVER the target's.
 *
 * The ledger entry it closes is ACC-W5-1: an Owner holding `WORKSPACE_MEMBERS` could reset
 * the Operator's password, sign in as them and reach the routes that refuse an Owner
 * directly. The ceiling was written for what an actor GRANTS and said nothing about whom an
 * actor ACTS UPON, so the credential right became a takeover.
 *
 * COVERED MEANS ALL FOUR PARTS: the target's `allSettings` short-circuit implies the
 * actor's, every flag the target holds is one the actor holds, no record verb of the
 * target's is wider than the actor's, and no object override of the target's is wider than
 * what the actor holds on that object. `Operator` covers everybody, by the same
 * short-circuit that makes it the last resort.
 *
 * THE OBJECT HALF IS ASKED OF THE UNION OF BOTH SIDES' KEYS (W5-F4). Asking it of the
 * TARGET's keys alone read the comparison from the wrong side: an actor is `objects` where
 * they are RESTRICTED, so an administrator with role-wide read and one narrowing override on
 * `invoice` deviates on `invoice` while an unrestricted Analyst does not - and with only the
 * target's keys walked, the one object the actor may not read never entered the comparison
 * and the reset was permitted into exactly the data the override excludes. `scopeFor`
 * answers for both sides, so an object neither of them overrides costs nothing.
 */
export function coverageViolations(
  actor: PermissionSet,
  target: PermissionSet,
): readonly GrantViolation[] {
  if (actor.allSettings) return [];
  const violations: GrantViolation[] = [];

  if (target.allSettings) violations.push({ kind: 'allSettings' });
  for (const flag of target.flags) {
    if (!hasFlag(actor, flag)) violations.push({ kind: 'flag', flag });
  }

  for (const verb of RECORD_VERBS) {
    const held = actor.records[verb];
    if (widerScope(held, target.records[verb]) !== held) {
      violations.push({ kind: 'record', verb });
    }
  }
  /**
   * EVERY OBJECT EITHER SIDE DEVIATES ON, against what the other holds THERE - which is
   * `scopeFor` rather than `objects[id]`, because a principal with no override on that
   * object still holds their role-wide scope on it. `includeUnassigned` contributes keys of
   * its own: a role can carry the unassigned flag on an object whose four scopes are the
   * role-wide ones, and that object still has to be compared.
   */
  const deviations = new Set([
    ...Object.keys(actor.objects),
    ...Object.keys(target.objects),
    ...Object.keys(actor.includeUnassigned),
    ...Object.keys(target.includeUnassigned),
  ]);
  for (const objectMetadataId of deviations) {
    for (const verb of RECORD_VERBS) {
      const held = scopeFor(actor, objectMetadataId, verb);
      const wanted = scopeFor(target, objectMetadataId, verb);
      if (widerScope(held, wanted) !== held) {
        violations.push({ kind: 'object', verb, objectMetadataId });
        continue;
      }
      /**
       * THE UNASSIGNED ROWS ARE PART OF THE SCOPE RATHER THAN A FLAG BESIDE IT (W5-F4).
       * `own` with `includeUnassigned` reaches every row nobody is assigned to
       * (`records/access.ts:210`, `records/query-plan.ts:365`), and `own` without it reaches
       * none of them - so two `own` scopes are NOT equal access, and comparing the scope
       * alone let a credential reset carry the actor into records they cannot open
       * themselves. Only `all` covers it from above; `none` and `team` reach nothing, so
       * neither side's flag changes anything there.
       */
      if (
        wanted === 'own' &&
        held !== 'all' &&
        includeUnassignedFor(target, objectMetadataId) &&
        !includeUnassignedFor(actor, objectMetadataId)
      ) {
        violations.push({ kind: 'unassigned', verb, objectMetadataId });
      }
    }
  }
  return violations;
}

// --- the seeded roles (section 3) ------------------------------------------

export const SEEDED_ROLE_LABELS = [
  'Operator',
  'Owner',
  'Coach',
  'Sales',
  'Setter',
  'Analyst',
  'Agent',
  'Agent (read, propose, import)',
] as const;

export type SeededRoleLabel = (typeof SEEDED_ROLE_LABELS)[number];

export interface SeededRole {
  readonly label: SeededRoleLabel;
  readonly description: string;
  readonly isEditable: boolean;
  readonly canUpdateAllSettings: boolean;
  readonly canBeAssignedToUsers: boolean;
  readonly canBeAssignedToApiKeys: boolean;
  readonly canReadAllObjectRecords: boolean;
  readonly canUpdateAllObjectRecords: boolean;
  readonly canSoftDeleteAllObjectRecords: boolean;
  readonly canDestroyAllObjectRecords: boolean;
  readonly flags: readonly PermissionFlag[];
  /**
   * OBJECTS THIS ROLE MAY NOT READ, by API NAME (fulfillment wave P1).
   *
   * By name because a role is seeded by migration 0013, before any object exists, and an
   * object override keys on the object's uuid (`meta.role_object_permission`). So the
   * migration ignores this list and `seed:ofmos` applies it once the objects are there,
   * as a `Read records: None` override per object - get-or-create, never overwriting an
   * override somebody already set in Settings. A name the model does not carry is skipped.
   */
  readonly unreadableObjects?: readonly string[];
}

/**
 * A STARTING CONFIGURATION, NOT A TAXONOMY (section 3). Everything except `Operator` is
 * editable, and `Sales` and `Setter` are seeded although nobody holds them yet.
 *
 * `Operator` sets `canBeAssignedToApiKeys: false`, which is a DELIBERATE DIVERGENCE from
 * Twenty (D13): `admin-area.md` D3 says a credential may not mint or reset a credential,
 * and denying the top role to keys closes at role level what `requireHumanSession` closes
 * at route level.
 *
 * `Agent` is the inverse - keys only, never people - and reads everything while writing
 * only where an object override says it may. Its grants are named per instance, so the
 * seed ships none: a fresh instance has no objects to name.
 *
 * `Agent (read, propose, import)` IS THE SECOND AGENT PRESET (wave 5 CA), and it exists
 * because the first one is not usable on the day it is picked. `Agent` names no writable
 * object, so an assistant holding it reads everything and can propose NOTHING until an
 * administrator has opened objects one by one - which is a correct default and a bad
 * starting point for the one thing the owner asked for. This preset is the starting point:
 * read everything, propose everything (a proposal is not a write - INV-10 queues every one
 * of them for a person), and import a file, which is what `IMPORT_CSV` is doing on a list
 * the owner's 2026-08-04 decision otherwise limits to Operator and Owner. It deliberately
 * does NOT hold `PENDING_DECIDE`: a key that could decide pending changes could approve its
 * own proposals, which would make the queue a formality. It holds no settings flag, cannot
 * manage people, cannot erase, and cannot be held by a person.
 */
export const SEEDED_ROLES: readonly SeededRole[] = [
  {
    label: 'Operator',
    description:
      'The technical operator of this instance. Holds every permission, including ones added later, and cannot be edited.',
    isEditable: false,
    canUpdateAllSettings: true,
    canBeAssignedToUsers: true,
    canBeAssignedToApiKeys: false,
    canReadAllObjectRecords: true,
    canUpdateAllObjectRecords: true,
    canSoftDeleteAllObjectRecords: true,
    canDestroyAllObjectRecords: true,
    flags: PERMISSION_FLAGS,
  },
  {
    label: 'Owner',
    description:
      'Runs the business side of the CRM: the data model, workflows, people, access roles, integration keys and imports. Cannot change webhooks or security settings, and cannot erase records.',
    isEditable: true,
    canUpdateAllSettings: false,
    canBeAssignedToUsers: true,
    canBeAssignedToApiKeys: true,
    canReadAllObjectRecords: true,
    canUpdateAllObjectRecords: true,
    canSoftDeleteAllObjectRecords: true,
    canDestroyAllObjectRecords: false,
    flags: [
      'DATA_MODEL',
      'WORKSPACE_MEMBERS',
      'WORKFLOWS_MANAGE',
      'WORKFLOWS_RUN',
      'IDENTITY',
      'PENDING_DECIDE',
      'ROLES',
      /**
       * The owner's decision of 2026-09-16 (roles fixed: Ben Owner, Arif Operator, coaches
       * Coach, setters Setter): credentials belong to the Owner, so `API_KEYS` joins the
       * `WORKSPACE_MEMBERS` he already held. `WEBHOOKS`, `AGENT_POLICY` and `SECURITY` do
       * not, and neither does `canDestroyAllObjectRecords`.
       */
      'API_KEYS',
      /**
       * Arif, 2026-08-01 for export and 2026-08-04 for import, both re-confirmed directly
       * in the main chat on 2026-08-04 (`csv-import-export.md` D33, section 15 questions 1
       * and 7): bulk in and bulk out belong to `Operator` and `Owner` only. `Operator`
       * needs no entry - `canUpdateAllSettings` answers every flag check - and Coach,
       * Sales, Setter, Analyst and Agent get neither.
       */
      'IMPORT_CSV',
      'EXPORT_CSV',
      /**
       * `reports.md` T6: everyone with a session READS reports, and creating, editing or
       * deleting one is the same line the owner drew for CSV export - Operator and Owner.
       * `Operator` needs no entry here for the reason the two CSV flags give above.
       */
      'REPORTS_MANAGE',
      /**
       * `inbound-intake.md` D2: configuring an inbound source is a settings act, and it is
       * the same line the flags above draw - Operator and Owner. `INTAKE_SUBMIT` is here
       * too, and it is the one grant on this list that a person will never USE: the receipt
       * route additionally requires `auth.kind === 'api_key'` and the key BOUND to the
       * source, so holding the flag on a session grants nothing. It is granted because the
       * escalation ceiling is a question about what the grantor HOLDS (guardrail 1), and an
       * Owner who cannot hold `INTAKE_SUBMIT` cannot mint the key that needs it.
       */
      'INTAKE_MANAGE',
      'INTAKE_SUBMIT',
      /**
       * `phase-b-coach.md` D12: the thresholds behind the colour are configuration, and
       * editing them is the same Operator-and-Owner line. `Operator` needs no entry here
       * for the reason the flags above give.
       */
      'HEALTH_MANAGE',
    ],
  },
  {
    label: 'Coach',
    description: 'Works records day to day, runs workflows on them and decides pending changes.',
    isEditable: true,
    canUpdateAllSettings: false,
    canBeAssignedToUsers: true,
    canBeAssignedToApiKeys: true,
    canReadAllObjectRecords: true,
    canUpdateAllObjectRecords: true,
    canSoftDeleteAllObjectRecords: true,
    canDestroyAllObjectRecords: false,
    flags: ['WORKFLOWS_RUN', 'PENDING_DECIDE'],
    /**
     * COACHES DO NOT SEE MONEY OR SALES (fulfillment concept, decision 4 and section 11):
     * payments, deals, leads, wins and phase transitions. The objects leave a coach's
     * sidebar, and the Money panel and the Billing section disappear by themselves, because
     * both read `payment` through the coach's own permissions. Everything else stays.
     */
    unreadableObjects: ['payment', 'deal', 'lead', 'win', 'phase_transition'],
  },
  {
    label: 'Sales',
    description: 'Sells: reads and edits records, runs workflows, decides pending changes.',
    isEditable: true,
    canUpdateAllSettings: false,
    canBeAssignedToUsers: true,
    canBeAssignedToApiKeys: true,
    canReadAllObjectRecords: true,
    canUpdateAllObjectRecords: true,
    canSoftDeleteAllObjectRecords: true,
    canDestroyAllObjectRecords: false,
    flags: ['WORKFLOWS_RUN', 'PENDING_DECIDE'],
  },
  {
    label: 'Setter',
    description: 'Books appointments: reads and edits records, and decides pending changes.',
    isEditable: true,
    canUpdateAllSettings: false,
    canBeAssignedToUsers: true,
    canBeAssignedToApiKeys: true,
    canReadAllObjectRecords: true,
    canUpdateAllObjectRecords: true,
    canSoftDeleteAllObjectRecords: false,
    canDestroyAllObjectRecords: false,
    flags: ['PENDING_DECIDE'],
  },
  {
    label: 'Analyst',
    description: 'Reads everything and changes nothing.',
    isEditable: true,
    canUpdateAllSettings: false,
    canBeAssignedToUsers: true,
    canBeAssignedToApiKeys: true,
    canReadAllObjectRecords: true,
    canUpdateAllObjectRecords: false,
    canSoftDeleteAllObjectRecords: false,
    canDestroyAllObjectRecords: false,
    flags: [],
  },
  {
    label: 'Agent',
    description:
      'The AI assistant. Reads every object and writes only the ones this role names explicitly. Cannot be held by a person.',
    isEditable: true,
    canUpdateAllSettings: false,
    canBeAssignedToUsers: false,
    canBeAssignedToApiKeys: true,
    canReadAllObjectRecords: true,
    canUpdateAllObjectRecords: false,
    canSoftDeleteAllObjectRecords: false,
    canDestroyAllObjectRecords: false,
    flags: [],
  },
  {
    label: 'Agent (read, propose, import)',
    description:
      'For an assistant connected to this CRM. Reads every object, proposes every change for a person to approve, and may import a file. Cannot decide pending changes, so it can never approve its own proposal, and cannot change a setting, manage people or erase a record. Cannot be held by a person.',
    isEditable: true,
    canUpdateAllSettings: false,
    canBeAssignedToUsers: false,
    canBeAssignedToApiKeys: true,
    canReadAllObjectRecords: true,
    /**
     * TRUE, AND IT IS NOT A DIRECT WRITE. `mayEver(objectId, verb)` is the AUTHORIZATION
     * half of D15.1 and answers whether a write is permitted at all; `agent_write_policy`
     * is the ROUTING half and decides whether a permitted one applies directly. The brake
     * lives in the second half and is untouched here - every record write this role makes
     * is still a proposal in the queue unless an administrator has opted one object out AND
     * the instance-wide switch is off.
     */
    canUpdateAllObjectRecords: true,
    canSoftDeleteAllObjectRecords: true,
    /** Erasure is nobody's default and not even the Owner's (protocol 0.2). */
    canDestroyAllObjectRecords: false,
    flags: ['IMPORT_CSV'],
  },
];

// --- API contracts ---------------------------------------------------------

export const roleObjectPermissionSchema = z.strictObject({
  objectMetadataId: z.uuid(),
  canRead: recordScopeSchema.nullable().default(null),
  canUpdate: recordScopeSchema.nullable().default(null),
  canSoftDelete: recordScopeSchema.nullable().default(null),
  canDestroy: recordScopeSchema.nullable().default(null),
  includeUnassigned: z.boolean().nullable().default(null),
});

export const ROLE_LABEL_MAX = 60;
export const ROLE_DESCRIPTION_MAX = 400;

export const createRoleRequestSchema = z.strictObject({
  label: z.string().min(1).max(ROLE_LABEL_MAX),
  description: z.string().max(ROLE_DESCRIPTION_MAX).nullable().default(null),
  canBeAssignedToUsers: z.boolean().default(true),
  canBeAssignedToApiKeys: z.boolean().default(true),
  canReadAllObjectRecords: z.boolean().default(false),
  canUpdateAllObjectRecords: z.boolean().default(false),
  canSoftDeleteAllObjectRecords: z.boolean().default(false),
  canDestroyAllObjectRecords: z.boolean().default(false),
  flags: z.array(permissionFlagSchema).default([]),
  objectPermissions: z.array(roleObjectPermissionSchema).default([]),
  /** Clone source, so "start from Coach" is one call rather than a read plus a create. */
  cloneOfRoleId: z.uuid().optional(),
});

export type CreateRoleRequest = z.infer<typeof createRoleRequestSchema>;

/**
 * Every field optional and an empty body a legal no-op, exactly like
 * `updateUserRequestSchema`: the screen sends the one thing that changed.
 *
 * `canUpdateAllSettings` is ABSENT on purpose. It is `Operator`'s short-circuit and
 * `Operator` is not editable (guardrail 3), so a route that accepted it would be offering
 * the one field guardrail 1 exists to make ungrantable.
 */
export const updateRoleRequestSchema = z.strictObject({
  label: z.string().min(1).max(ROLE_LABEL_MAX).optional(),
  description: z.string().max(ROLE_DESCRIPTION_MAX).nullable().optional(),
  canBeAssignedToUsers: z.boolean().optional(),
  canBeAssignedToApiKeys: z.boolean().optional(),
  canReadAllObjectRecords: z.boolean().optional(),
  canUpdateAllObjectRecords: z.boolean().optional(),
  canSoftDeleteAllObjectRecords: z.boolean().optional(),
  canDestroyAllObjectRecords: z.boolean().optional(),
  flags: z.array(permissionFlagSchema).optional(),
  objectPermissions: z.array(roleObjectPermissionSchema).optional(),
});

export type UpdateRoleRequest = z.infer<typeof updateRoleRequestSchema>;

/** Whole-set assignment: the body is the roles the principal should end up holding. */
export const assignRolesRequestSchema = z.strictObject({
  roleIds: z.array(z.uuid()),
});

export type AssignRolesRequest = z.infer<typeof assignRolesRequestSchema>;

/**
 * Section 6: a member row is a PRINCIPAL, person or key, because splitting principals
 * across two screens is what makes "who can do X" hard to answer.
 */
export type PrincipalKind = 'user' | 'apiKey';

export interface RoleHolder {
  readonly kind: PrincipalKind;
  readonly id: string;
  readonly label: string;
  /** Users only. */
  readonly email?: string | undefined;
  readonly isActive: boolean;
  readonly isAgent?: boolean | undefined;
  readonly roles: readonly RoleSummary[];
}

/** UI grouping for the permissions section of role detail (section 6, D3 grouping). */
export interface PermissionFlagDescriptor {
  readonly flag: PermissionFlag;
  readonly label: string;
  readonly description: string;
  readonly group: 'Data' | 'People and access' | 'Automation' | 'Integrations' | 'Instance';
}

export const PERMISSION_FLAG_DESCRIPTORS: readonly PermissionFlagDescriptor[] = [
  {
    flag: 'DATA_MODEL',
    label: 'Data model',
    description: 'Create and change objects and fields.',
    group: 'Data',
  },
  {
    flag: 'IDENTITY',
    label: 'Identity resolution',
    description: 'Manage identifier types and duplicate detection, and undo a merge.',
    group: 'Data',
  },
  {
    flag: 'PENDING_DECIDE',
    label: 'Decide pending changes',
    description: 'Approve or reject a proposed change.',
    group: 'Data',
  },
  /**
   * THE DESCRIPTION CARRIES THE DEFAULT, NOT THE DECISION BEHIND IT (`csv-import-export.md`
   * D33; wave 3 S-C3). Roles are "All editable except `Operator`", so the moment these
   * became ordinary flags any holder of `ROLES` could grant them onward with no migration
   * and no further decision - the owner's lock became a default. Making it structural would
   * need a "locked flag" concept this file does not have, so the answer is to leave it
   * grantable and make the grant VISIBLE: the person about to hand bulk export to an Analyst
   * reads what they are changing, in the grant UI, at the moment they change it.
   *
   * WHAT THE READER GETS IS THE DEFAULT IN PLAIN WORDS. Until 2026-09-10 these three
   * sentences named a person and a date - "Arif decided on 2026-08-04 that this belongs to
   * Operator and Owner" - which is a note from our own record, in a settings pane, to an
   * operator who has never heard of either. Apple's rule: "Keep settings labels clear and
   * simple ... If the setting label isn't enough, add an explanation", and "Choose words
   * that are easily understood" (hig: https://developer.apple.com/design/human-interface-guidelines/writing).
   * The fact the reader needs is which roles hold it, and that is now what the sentence says.
   */
  {
    flag: 'IMPORT_CSV',
    label: 'Import from CSV',
    description:
      'Upload a CSV file and write its rows into an object, and undo the records an import created. Only Operator and Owner hold this by default.',
    group: 'Data',
  },
  {
    flag: 'EXPORT_CSV',
    label: 'Export to CSV',
    description:
      'Download the records a screen is showing as a CSV file. Only Operator and Owner hold this by default.',
    group: 'Data',
  },
  {
    flag: 'REPORTS_MANAGE',
    label: 'Build reports',
    description:
      'Create, change and delete the reports everyone can see. Reading them needs no permission at all. Only Operator and Owner hold this by default.',
    group: 'Data',
  },
  {
    flag: 'HEALTH_MANAGE',
    label: 'Health rules',
    description:
      'Change the rules that decide a customer is green, yellow or red, and how long an assessment a coach gives outranks them. Reading a health score needs no permission.',
    group: 'Data',
  },
  {
    flag: 'WORKSPACE_MEMBERS',
    label: 'Members',
    description: 'Create people, change their details and reset their passwords.',
    group: 'People and access',
  },
  {
    flag: 'ROLES',
    label: 'Access roles',
    description: 'Create and change access roles, and assign them to people and keys.',
    group: 'People and access',
  },
  {
    flag: 'WORKFLOWS_MANAGE',
    label: 'Manage workflows',
    description: 'Create, edit, activate and delete workflows.',
    group: 'Automation',
  },
  {
    flag: 'WORKFLOWS_RUN',
    label: 'Run workflows',
    description: 'Run a workflow on a record, and rerun one.',
    group: 'Automation',
  },
  {
    flag: 'AGENT_POLICY',
    label: 'Agent write policy',
    description: 'Decide which objects the agent may write directly, and use the kill switch.',
    group: 'Automation',
  },
  {
    flag: 'API_KEYS',
    label: 'API keys',
    description: 'Create and revoke integration keys.',
    group: 'Integrations',
  },
  {
    flag: 'WEBHOOKS',
    label: 'Webhooks',
    description: 'Create, change, test and replay outbound webhooks.',
    group: 'Integrations',
  },
  {
    flag: 'INTAKE_MANAGE',
    label: 'Inbound sources',
    description:
      'Create and change the sources that deliver into this CRM: their mapping, their schedule, their credential and their key.',
    group: 'Integrations',
  },
  {
    flag: 'INTAKE_SUBMIT',
    label: 'Deliver to an inbound source',
    description:
      'Post a delivery to a source. Held by the key a source mints for itself, which may deliver only to that one source; a person holding it can still deliver nothing.',
    group: 'Integrations',
  },
  {
    flag: 'SECURITY',
    label: 'Security',
    description: 'Read the instance panel and change runtime switches.',
    group: 'Instance',
  },
];

export const PERMISSION_FLAG_GROUPS = [
  'Data',
  'People and access',
  'Automation',
  'Integrations',
  'Instance',
] as const;

export function flagDescriptor(flag: PermissionFlag): PermissionFlagDescriptor {
  const found = PERMISSION_FLAG_DESCRIPTORS.find((entry) => entry.flag === flag);
  if (found === undefined) throw new Error(`no descriptor for permission flag ${flag}`);
  return found;
}

/** Copy for a denied toggle (section 6): "Only a role with Security can grant this." */
export function denialCopy(flag: PermissionFlag): string {
  return `Only a role with ${flagDescriptor(flag).label} can grant this.`;
}

export const RECORD_VERB_LABELS: Readonly<Record<RecordVerb, string>> = {
  read: 'Read records',
  update: 'Create and edit records',
  softDelete: 'Delete records',
  destroy: 'Erase records permanently',
};

export const RECORD_SCOPE_LABELS: Readonly<Record<RecordScope, string>> = {
  all: 'All records',
  team: 'Their team',
  own: 'Only their own',
  none: 'None',
};

/**
 * The union of the seeded `Operator`, as a constant.
 *
 * It exists for TESTS and for the seed, so neither hand-builds a `PermissionSet` and gets
 * `allSettings` wrong - which is the one field that silently makes every check pass.
 */
export const OPERATOR_PERMISSIONS: PermissionSet = {
  allSettings: true,
  flags: [...PERMISSION_FLAGS].sort(),
  records: { read: 'all', update: 'all', softDelete: 'all', destroy: 'all' },
  objects: {},
  includeUnassigned: {},
};
